import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  List,
  Popconfirm,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import {
  AlertOutlined,
  ArrowLeftOutlined,
  BookOutlined,
  DeleteOutlined,
  PaperClipOutlined,
  SaveOutlined,
  TagsOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import RichTextEditor from "components/common/RichTextEditor";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { useAuth } from "contexts/AuthContext";
import { useFileUpload } from "hooks/useFileUpload";
import useUsers from "hooks/userManagement/useUsers";
import * as pmService from "services/pmService";
import {
  clearRichTextImageMaps,
  createRichTextImageMaps,
  normalizeRichTextImagesForSave,
  processRichTextImagesForEdit,
  uploadRichTextImage,
} from "utils/richTextImages";
import { OLIVEEB_COLORS } from "workspaces/colors";
import paths from "nav/paths";

const { Text } = Typography;

const GUIDE_STATUSES = ["DRAFT", "ACTIVE", "ARCHIVED"];
const INCIDENT_STATUSES = ["OPEN", "INVESTIGATING", "MITIGATED", "RESOLVED", "CLOSED"];
const INCIDENT_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const TICKET_STATUSES = ["OPEN", "IN_PROGRESS", "BLOCKED", "RESOLVED", "CLOSED", "CANCELLED"];
const TICKET_PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];
const TICKET_TYPES = ["SUPPORT", "INCIDENT", "CHANGE", "TASK"];
const DATE_FIELDS = ["detectedAt", "mitigatedAt", "resolvedAt", "dueAt", "closedAt"];
const EMAIL_LIST_FIELDS = ["escalationContacts"];
const RICH_TEXT_FIELDS_BY_RESOURCE = {
  guides: ["description", "symptoms", "steps", "verificationSteps", "rollbackSteps"],
  incidents: ["description", "impact", "rootCause", "resolution"],
  tickets: ["description"],
};

const pageItems = (page) => (Array.isArray(page?.content) ? page.content : []);

const labelize = (value = "") =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const optionList = (items) => items.map((item) => ({ value: item, label: labelize(item) }));

const recordNumber = (record = {}) => record.number || record.key || "";

const removeEmpty = (payload) =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );

const toFormValues = (record = {}) => {
  const values = { ...record };
  DATE_FIELDS.forEach((field) => {
    values[field] = values[field] ? dayjs(values[field]) : null;
  });
  EMAIL_LIST_FIELDS.forEach((field) => {
    if (typeof values[field] === "string") {
      values[field] = values[field]
        .split(/[,;\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }
  });
  return values;
};

const toApiPayload = (values = {}, isEditMode) => {
  const payload = { ...values };
  DATE_FIELDS.forEach((field) => {
    if (payload[field]?.toISOString) {
      payload[field] = payload[field].toISOString();
    }
  });
  EMAIL_LIST_FIELDS.forEach((field) => {
    if (Array.isArray(payload[field])) {
      payload[field] = payload[field].join(", ");
    }
  });
  delete payload.key;
  delete payload.number;
  return removeEmpty(payload);
};

const attachmentDisplayName = (attachment = {}) =>
  attachment.label || attachment.fileName || attachment.file?.filename || attachment.fileId || "Attachment";

const toAttachmentPayload = (attachment = {}) => ({
  fileId: attachment.fileId,
  fileName: attachment.fileName || attachmentDisplayName(attachment),
  label: attachment.label || attachment.fileName || attachmentDisplayName(attachment),
});

const resourceConfig = {
  guides: {
    title: "Troubleshooting Guide",
    pluralTitle: "Troubleshooting Guides",
    route: paths.troubleshootingGuides,
    idParam: "guideId",
    icon: <BookOutlined />,
    get: pmService.getTroubleshootingGuide,
    create: pmService.createTroubleshootingGuide,
    update: pmService.updateTroubleshootingGuide,
    attachmentResourcePath: "troubleshooting-guides",
    defaults: { status: "DRAFT" },
    subtitle: "Document symptoms, diagnosis steps, verification, rollback, and escalation paths.",
  },
  incidents: {
    title: "Incident",
    pluralTitle: "Incidents",
    route: paths.incidents,
    idParam: "incidentId",
    icon: <AlertOutlined />,
    get: pmService.getIncident,
    create: pmService.createIncident,
    update: pmService.updateIncident,
    attachmentResourcePath: "incidents",
    defaults: { status: "OPEN", severity: "MEDIUM" },
    subtitle: "Record operational events with impact, ownership, timeline, and resolution detail.",
  },
  tickets: {
    title: "Ticket",
    pluralTitle: "Tickets",
    route: paths.tickets,
    idParam: "ticketId",
    icon: <TagsOutlined />,
    get: pmService.getTicket,
    create: pmService.createTicket,
    update: pmService.updateTicket,
    attachmentResourcePath: "tickets",
    defaults: { type: "SUPPORT", status: "OPEN", priority: "MEDIUM" },
    subtitle: "Manage support, incident, change, and task requests with clear ownership.",
  },
};

const editorFieldStyle = {
  border: `1px solid ${OLIVEEB_COLORS.border}`,
  borderRadius: 8,
  overflow: "hidden",
  background: OLIVEEB_COLORS.white,
};

const SectionTitle = ({ title, description }) => (
  <div style={{ marginBottom: 16 }}>
    <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkGreen, fontSize: 15 }}>
      {title}
    </Text>
    {description ? (
      <div style={{ color: OLIVEEB_COLORS.textMuted, fontSize: 12.5, marginTop: 4 }}>
        {description}
      </div>
    ) : null}
  </div>
);

const RichTextFormItem = ({
  name,
  label,
  placeholder,
  required = false,
  height = "220px",
  onImageUpload,
}) => (
  <Form.Item
    name={name}
    label={label}
    rules={required ? [{ required: true, message: `${label} is required` }] : undefined}
  >
    <RichTextEditor
      placeholder={placeholder}
      height={height}
      className="operation-rich-editor"
      onImageUpload={onImageUpload}
      onImagePaste={onImageUpload}
    />
  </Form.Item>
);

const OperationAttachmentFormSection = ({
  attachments,
  loading,
  uploading,
  deletingAttachmentId,
  disabled,
  onUpload,
  onDelete,
}) => (
  <div
    style={{
      border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
      borderRadius: 8,
      background: OLIVEEB_COLORS.white,
      padding: 16,
      marginTop: 8,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: OLIVEEB_COLORS.oliveebDarkGreen,
          fontWeight: 700,
        }}
      >
        <PaperClipOutlined />
        <span>Attachments ({attachments.length})</span>
      </div>
      <Upload
        multiple
        showUploadList={false}
        disabled={disabled || uploading}
        customRequest={({ file, onError, onSuccess }) => {
          onUpload(file)
            .then(() => onSuccess?.("ok"))
            .catch((error) => onError?.(error));
        }}
      >
        <Button type="primary" icon={<UploadOutlined />} loading={uploading} disabled={disabled}>
          Attach file
        </Button>
      </Upload>
    </div>

    {loading ? (
      <div style={{ display: "flex", justifyContent: "center", padding: "18px 0" }}>
        <Spin />
      </div>
    ) : attachments.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No attachments yet" />
    ) : (
      <List
        dataSource={attachments}
        renderItem={(attachment) => {
          const name = attachment.label || attachment.fileName || attachment.fileId || "Attachment";
          const attachmentKey = attachment.id || attachment.fileId;
          return (
            <List.Item
              key={attachmentKey}
              actions={[
                <Popconfirm
                  key="delete"
                  title="Delete attachment?"
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: deletingAttachmentId === attachmentKey }}
                  onConfirm={() => onDelete(attachment)}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} disabled={disabled} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen, fontSize: 18 }} />}
                title={
                  attachment.fileUrl ? (
                    <a href={attachment.fileUrl} target="_blank" rel="noreferrer">
                      {name}
                    </a>
                  ) : (
                    <span>{name}</span>
                  )
                }
                description={attachment.pending ? "Will attach when this record is saved" : attachment.fileName}
              />
            </List.Item>
          );
        }}
      />
    )}
  </div>
);

const EmailLookupSelect = ({
  mode,
  value,
  onChange,
  options,
  onSearch,
  loading,
  placeholder = "Search by email",
  valueKind = "email",
}) => (
  <Select
    allowClear
    showSearch
    mode={mode}
    value={value}
    onChange={onChange}
    onSearch={onSearch}
    options={options}
    loading={loading}
    filterOption={false}
    placeholder={placeholder}
    notFoundContent="Type at least 3 characters to search by email"
    optionFilterProp="label"
    tokenSeparators={mode ? [",", ";", " "] : undefined}
    tagRender={mode ? undefined : undefined}
  />
);

const OperationFormPage = ({ resource }) => {
  const config = resourceConfig[resource] || resourceConfig.guides;
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { uploadFile } = useFileUpload();
  const { searchUserProfiles, getUserById, loading: usersLoading } = useUsers(
    0,
    20,
    null,
    true,
    { autoFetch: false },
  );
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(Boolean(params[config.idParam]));
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [guides, setGuides] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  const richTextImageMapsRef = useRef(createRichTextImageMaps());
  const richTextUploadsInFlightRef = useRef(0);

  const recordId = params[config.idParam];
  const isEditMode = Boolean(recordId);
  const actorId = user?.id || user?.userId || user?.uid || "";
  const supportsAttachments = Boolean(config.attachmentResourcePath);
  const richTextFields = useMemo(
    () => RICH_TEXT_FIELDS_BY_RESOURCE[resource] || ["description"],
    [resource],
  );

  const projectOptions = projects.map((project) => ({
    value: project.id,
    label: project.name || project.code || project.id,
  }));
  const guideOptions = guides.map((guide) => ({
    value: guide.id,
    label: `${recordNumber(guide) ? `${recordNumber(guide)} - ` : ""}${guide.title}`,
  }));
  const incidentOptions = incidents.map((incident) => ({
    value: incident.id,
    label: `${recordNumber(incident) ? `${recordNumber(incident)} - ` : ""}${incident.title}`,
  }));

  const normalizeUserOption = (profile = {}) => {
    const email = profile.email && profile.email !== "None" ? profile.email : "";
    const name = profile.fullName && profile.fullName !== "None" ? profile.fullName : "";
    return {
      value: profile.id,
      email,
      label: email ? `${email}${name ? ` - ${name}` : ""}` : profile.id,
    };
  };

  const searchUsersByEmail = useCallback(
    async (query = "") => {
      const term = String(query || "").trim();
      if (term.length < 3) {
        setUserOptions([]);
        return;
      }

      const results = (await searchUserProfiles(term, "ALL", 0)) || [];
      setUserOptions(
        results
          .filter((profile) =>
            String(profile?.email || "")
              .toLowerCase()
              .includes(term.toLowerCase()),
          )
          .map(normalizeUserOption),
      );
    },
    [searchUserProfiles],
  );

  const emailOptions = userOptions
    .filter((option) => option.email)
    .map((option) => ({ value: option.email, label: option.label }));
  const idOptions = userOptions.map((option) => ({ value: option.value, label: option.label }));

  const processRichTextFieldsForForm = useCallback(
    async (values = {}) => {
      const nextValues = { ...values };
      await Promise.all(
        richTextFields.map(async (field) => {
          if (!nextValues[field]) return;
          nextValues[field] = await processRichTextImagesForEdit(
            nextValues[field],
            richTextImageMapsRef.current,
          );
        }),
      );
      return nextValues;
    },
    [richTextFields],
  );

  const handleRichTextImageUpload = useCallback(
    async (fieldName, file, helpers) => {
      richTextUploadsInFlightRef.current += 1;
      try {
        await uploadRichTextImage({
          file,
          helpers,
          uploadFile,
          userId: actorId,
          maps: richTextImageMapsRef.current,
          onHtmlChange: (html) => form.setFieldsValue({ [fieldName]: html }),
        });
      } catch (error) {
        message.error(error?.message || "Failed to upload image.");
      } finally {
        richTextUploadsInFlightRef.current = Math.max(
          0,
          richTextUploadsInFlightRef.current - 1,
        );
      }
    },
    [actorId, form, uploadFile],
  );

  const renderRichTextFormItem = useCallback(
    (props) => (
      <RichTextFormItem
        {...props}
        onImageUpload={(file, helpers) =>
          handleRichTextImageUpload(props.name, file, helpers)
        }
      />
    ),
    [handleRichTextImageUpload],
  );

  const loadAttachments = useCallback(async () => {
    if (!supportsAttachments || !isEditMode || !recordId) {
      setAttachments([]);
      return;
    }

    setAttachmentsLoading(true);
    try {
      const data = await pmService.listOperationAttachments(config.attachmentResourcePath, recordId);
      setAttachments(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error(error?.response?.data?.message || error?.message || "Failed to load attachments.");
    } finally {
      setAttachmentsLoading(false);
    }
  }, [config.attachmentResourcePath, isEditMode, recordId, supportsAttachments]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleUploadAttachment = useCallback(
    async (file) => {
      if (!supportsAttachments || !file) return;
      if (!actorId) {
        message.error("User ID is required to attach files.");
        return;
      }

      setAttachmentUploading(true);
      try {
        const uploaded = await uploadFile(actorId, file, {
          purpose: "USER_PROFILE",
          fileType: file.type?.startsWith("image/") ? "PHOTO" : "DOCUMENT",
        });
        const payload = {
          fileId: uploaded.fileId,
          fileName: file.name,
          label: file.name,
        };

        if (isEditMode && recordId) {
          const created = await pmService.addOperationAttachments(
            config.attachmentResourcePath,
            recordId,
            [payload],
            actorId,
          );
          setAttachments((current) => [...(Array.isArray(created) ? created : []), ...current]);
          message.success("File attached.");
          return;
        }

        setAttachments((current) => [
          {
            ...payload,
            id: `pending-${uploaded.fileId}`,
            pending: true,
          },
          ...current,
        ]);
        message.success("File ready to attach when this record is saved.");
      } catch (error) {
        message.error(error?.response?.data?.message || error?.message || "Failed to attach file.");
        throw error;
      } finally {
        setAttachmentUploading(false);
      }
    },
    [actorId, config.attachmentResourcePath, isEditMode, recordId, supportsAttachments, uploadFile],
  );

  const handleDeleteAttachment = useCallback(
    async (attachment = {}) => {
      if (!supportsAttachments) return;
      const attachmentKey = attachment.id || attachment.fileId;
      if (!attachmentKey) return;

      if (attachment.pending) {
        setAttachments((current) =>
          current.filter((item) => (item.id || item.fileId) !== attachmentKey),
        );
        return;
      }

      setDeletingAttachmentId(attachmentKey);
      try {
        await pmService.deleteOperationAttachment(
          config.attachmentResourcePath,
          recordId,
          attachment.id,
          actorId,
        );
        setAttachments((current) => current.filter((item) => item.id !== attachment.id));
        message.success("Attachment deleted.");
      } catch (error) {
        message.error(error?.response?.data?.message || error?.message || "Failed to delete attachment.");
      } finally {
        setDeletingAttachmentId(null);
      }
    },
    [actorId, config.attachmentResourcePath, recordId, supportsAttachments],
  );

  const hydrateAssigneeOption = useCallback(
    async (userId) => {
      if (!userId) return;
      try {
        const profile = await getUserById(userId);
        const option = normalizeUserOption(profile);
        setUserOptions((prev) =>
          prev.some((item) => String(item.value) === String(option.value))
            ? prev
            : [option, ...prev],
        );
      } catch {
        // Keep the raw id selectable if the profile service cannot hydrate it.
        setUserOptions((prev) =>
          prev.some((item) => String(item.value) === String(userId))
            ? prev
            : [{ value: userId, label: userId, email: "" }, ...prev],
        );
      }
    },
    [getUserById],
  );

  const loadReferences = useCallback(async () => {
    const requests = [];
    if (resource !== "guides") {
      requests.push(pmService.listProjects({ page: 0, size: 100, sort: "name,asc" }));
    } else {
      requests.push(Promise.resolve({ content: [] }));
    }
    if (resource === "incidents") {
      requests.push(pmService.listTroubleshootingGuides({ page: 0, size: 100, sort: "title,asc" }));
    } else {
      requests.push(Promise.resolve({ content: [] }));
    }
    if (resource === "tickets") {
      requests.push(pmService.listIncidents({ page: 0, size: 100, sort: "createdAt,desc" }));
    } else {
      requests.push(Promise.resolve({ content: [] }));
    }

    try {
      const [projectsPage, guidesPage, incidentsPage] = await Promise.all(requests);
      setProjects(pageItems(projectsPage));
      setGuides(pageItems(guidesPage));
      setIncidents(pageItems(incidentsPage));
    } catch {
      message.warning("Some reference lists could not be loaded");
    }
  }, [resource]);

  useEffect(() => {
    clearRichTextImageMaps(richTextImageMapsRef.current);
    form.setFieldsValue(config.defaults);
    loadReferences();
  }, [config.defaults, form, loadReferences]);

  useEffect(() => {
    let mounted = true;

    const loadRecord = async () => {
      if (!isEditMode) {
        clearRichTextImageMaps(richTextImageMapsRef.current);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        clearRichTextImageMaps(richTextImageMapsRef.current);
        const record = await config.get(recordId);
        if (mounted) {
          const nextValues = await processRichTextFieldsForForm({
            ...config.defaults,
            ...toFormValues(record),
          });
          form.setFieldsValue(nextValues);
          if (resource === "tickets" && record?.assigneeId) {
            hydrateAssigneeOption(record.assigneeId);
          }
        }
      } catch (error) {
        message.error(error?.message || `Failed to load ${config.title.toLowerCase()}`);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadRecord();

    return () => {
      mounted = false;
    };
  }, [config, form, hydrateAssigneeOption, isEditMode, processRichTextFieldsForForm, recordId, resource]);

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (richTextUploadsInFlightRef.current > 0) {
      message.warning("Please wait for image uploads to finish.");
      return;
    }

    const normalizedValues = { ...values };
    richTextFields.forEach((field) => {
      if (normalizedValues[field]) {
        normalizedValues[field] = normalizeRichTextImagesForSave(
          normalizedValues[field],
          richTextImageMapsRef.current,
        );
      }
    });
    const payload = toApiPayload(normalizedValues, isEditMode);
    const pendingAttachments = attachments
      .filter((attachment) => attachment.pending && attachment.fileId)
      .map(toAttachmentPayload);

    setSaving(true);
    try {
      if (isEditMode) {
        await config.update(recordId, payload, actorId);
        message.success(`${config.title} updated`);
      } else {
        const created = await config.create(payload, actorId);
        const createdId = created?.id;
        if (pendingAttachments.length > 0 && supportsAttachments && !createdId) {
          throw new Error(`${config.title} was saved, but the response did not include an ID for attachments.`);
        }
        if (pendingAttachments.length > 0 && supportsAttachments && createdId) {
          await pmService.addOperationAttachments(
            config.attachmentResourcePath,
            createdId,
            pendingAttachments,
            actorId,
          );
        }
        message.success(`${config.title} created`);
      }
      navigate(config.route);
    } catch (error) {
      message.error(error?.message || `Failed to save ${config.title.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  const renderGuideFields = () => (
    <>
      <SectionTitle
        title="Procedure"
        description="Use rich formatting for ordered steps, command snippets, and escalation notes."
      />
      <Form.Item name="systemArea" label="System Area">
        <Input placeholder="Authentication, payments, notifications" />
      </Form.Item>
      {renderRichTextFormItem({
        name: "symptoms",
        label: "Symptoms",
        placeholder: "Describe the signals that indicate this failure.",
      })}
      {renderRichTextFormItem({
        name: "steps",
        label: "Troubleshooting Steps",
        placeholder: "Write the procedure operators should follow.",
        required: true,
        height: "280px",
      })}
      {renderRichTextFormItem({
        name: "verificationSteps",
        label: "Verification Steps",
        placeholder: "Describe how to verify the system has recovered.",
      })}
      {renderRichTextFormItem({
        name: "rollbackSteps",
        label: "Rollback Steps",
        placeholder: "Document rollback or fail-safe actions.",
      })}
      <Form.Item name="escalationContacts" label="Escalation Contact Emails">
        <EmailLookupSelect
          mode="tags"
          options={emailOptions}
          onSearch={searchUsersByEmail}
          loading={usersLoading}
          placeholder="Search users by email or enter escalation emails"
        />
      </Form.Item>
      <Form.Item name="status" label="Status">
        <Select options={optionList(GUIDE_STATUSES)} />
      </Form.Item>
    </>
  );

  const renderIncidentFields = () => (
    <>
      <SectionTitle
        title="Incident Context"
        description="Capture impact, ownership, diagnosis, and the operational timeline."
      />
      <Form.Item name="projectId" label="Project">
        <Select allowClear showSearch optionFilterProp="label" options={projectOptions} />
      </Form.Item>
      <Form.Item name="affectedSystem" label="Affected System">
        <Input placeholder="API gateway, auth service, payment worker" />
      </Form.Item>
      {renderRichTextFormItem({ name: "impact", label: "Impact", placeholder: "Who or what was affected?" })}
      {renderRichTextFormItem({
        name: "rootCause",
        label: "Root Cause",
        placeholder: "Document confirmed or suspected root cause.",
      })}
      {renderRichTextFormItem({
        name: "resolution",
        label: "Resolution",
        placeholder: "Describe mitigation and final resolution.",
      })}
      <Form.Item name="reportedBy" label="Reported By Email">
        <EmailLookupSelect
          options={emailOptions}
          onSearch={searchUsersByEmail}
          loading={usersLoading}
          placeholder="Search reporter by email"
        />
      </Form.Item>
      <Form.Item name="assignedTo" label="Assigned To Email">
        <EmailLookupSelect
          options={emailOptions}
          onSearch={searchUsersByEmail}
          loading={usersLoading}
          placeholder="Search assignee by email"
        />
      </Form.Item>
      <Form.Item name="severity" label="Severity">
        <Select options={optionList(INCIDENT_SEVERITIES)} />
      </Form.Item>
      <Form.Item name="status" label="Status">
        <Select options={optionList(INCIDENT_STATUSES)} />
      </Form.Item>
      <Form.Item name="troubleshootingGuideId" label="Troubleshooting Guide">
        <Select allowClear showSearch optionFilterProp="label" options={guideOptions} />
      </Form.Item>
      <Space size={12} style={{ width: "100%" }} wrap>
        <Form.Item name="detectedAt" label="Detected At" style={{ flex: "1 1 220px" }}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="mitigatedAt" label="Mitigated At" style={{ flex: "1 1 220px" }}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="resolvedAt" label="Resolved At" style={{ flex: "1 1 220px" }}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
      </Space>
    </>
  );

  const renderTicketFields = () => (
    <>
      <SectionTitle
        title="Ticket Details"
        description="Define the request type, priority, ownership, and any incident relationship."
      />
      <Form.Item name="projectId" label="Project">
        <Select allowClear showSearch optionFilterProp="label" options={projectOptions} />
      </Form.Item>
      <Form.Item name="type" label="Type">
        <Select options={optionList(TICKET_TYPES)} />
      </Form.Item>
      <Form.Item name="status" label="Status">
        <Select options={optionList(TICKET_STATUSES)} />
      </Form.Item>
      <Form.Item name="priority" label="Priority">
        <Select options={optionList(TICKET_PRIORITIES)} />
      </Form.Item>
      <Form.Item name="incidentId" label="Linked Incident">
        <Select allowClear showSearch optionFilterProp="label" options={incidentOptions} />
      </Form.Item>
      <Form.Item name="assigneeId" label="Assignee Email">
        <EmailLookupSelect
          options={idOptions}
          onSearch={searchUsersByEmail}
          loading={usersLoading}
          placeholder="Search assignee by email"
          valueKind="id"
        />
      </Form.Item>
      <Form.Item name="requesterName" label="Requester Email">
        <EmailLookupSelect
          options={emailOptions}
          onSearch={searchUsersByEmail}
          loading={usersLoading}
          placeholder="Search requester by email"
        />
      </Form.Item>
      <Space size={12} style={{ width: "100%" }} wrap>
        <Form.Item name="dueAt" label="Due At" style={{ flex: "1 1 220px" }}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="closedAt" label="Closed At" style={{ flex: "1 1 220px" }}>
          <DatePicker showTime style={{ width: "100%" }} />
        </Form.Item>
      </Space>
    </>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 420 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={config.icon}
          title={`${isEditMode ? "Edit" : "New"} ${config.title}`}
          subtitle={config.subtitle}
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(config.route)}>
          Back to {config.pluralTitle}
        </Button>
      </div>

      <Card
        styles={{ body: { padding: 24 } }}
        style={{
          borderRadius: 8,
          borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
          boxShadow: `0 10px 28px ${OLIVEEB_COLORS.shadow}`,
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={config.defaults}
          style={editorFieldStyle}
        >
          <div style={{ padding: 20 }}>
            <SectionTitle
              title="Basics"
              description="The number is generated by the backend and appears on saved records."
            />
            <Form.Item name="title" label="Title" rules={[{ required: true, message: "Title is required" }]}>
              <Input placeholder={`Short ${config.title.toLowerCase()} title`} />
            </Form.Item>
            {renderRichTextFormItem({
              name: "description",
              label: "Description",
              placeholder: `Write a clear ${config.title.toLowerCase()} description.`,
              height: "200px",
            })}
            {resource === "guides" ? renderGuideFields() : null}
            {resource === "incidents" ? renderIncidentFields() : null}
            {resource === "tickets" ? renderTicketFields() : null}
            {supportsAttachments ? (
              <OperationAttachmentFormSection
                attachments={attachments}
                loading={attachmentsLoading}
                uploading={attachmentUploading}
                deletingAttachmentId={deletingAttachmentId}
                disabled={saving}
                onUpload={handleUploadAttachment}
                onDelete={handleDeleteAttachment}
              />
            ) : null}
          </div>
        </Form>

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button onClick={() => navigate(config.route)}>Cancel</Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSubmit}>
            Save {config.title}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default OperationFormPage;
